import React, { useEffect } from "react";
import {
  IonContent,
  IonPage,
  IonText,
  IonItem,
  IonLabel,
  IonBadge,
  IonSpinner,
  IonList,
} from "@ionic/react";
import { useOrganisations, useSwitchOrganisation } from "@/hooks";
import { getErrorMessage } from "@/api/client";
import type { Organisation } from "@/types";
import AppLogo from "@/components/AppLogo";
import "./Auth.css";

const Organisations: React.FC = () => {
  const { data, isLoading, error } = useOrganisations();
  const switchMutation = useSwitchOrganisation();

  // Auto-select if only one organisation
  useEffect(() => {
    if (data?.organisations && data.organisations.length === 1) {
      handleSelectOrganisation(data.organisations[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleSelectOrganisation = async (org: Organisation) => {
    try {
      await switchMutation.mutateAsync(org.id);
      // Navigation handled by the hook
    } catch {
      // Error handled by mutation
    }
  };

  const getRoleBadgeColor = (role: string) => {
    return role === "admin" ? "primary" : "medium";
  };

  const getRoleLabel = (role: string) => {
    return role === "admin" ? "Beheerder" : "Medewerker";
  };

  if (isLoading) {
    return (
      <IonPage>
        <IonContent className="auth-content ion-padding">
          <div className="auth-container">
            <IonSpinner name="crescent" />
            <IonText color="medium">
              <p>Organisaties laden...</p>
            </IonText>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (error) {
    return (
      <IonPage>
        <IonContent className="auth-content ion-padding">
          <div className="auth-container">
            <IonText color="danger">
              <p>{getErrorMessage(error)}</p>
            </IonText>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const organisations = data?.organisations || [];

  if (organisations.length === 0) {
    return (
      <IonPage>
        <IonContent className="auth-content ion-padding">
          <div className="auth-container">
            <div className="auth-header">
              <AppLogo />
              <IonText color="dark">
                <h1>Geen organisatie</h1>
                <p>
                  Je bent nog niet gekoppeld aan een organisatie.
                  <br />
                  Neem contact op met je beheerder.
                </p>
              </IonText>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  // If switching, show loading state
  if (switchMutation.isPending) {
    return (
      <IonPage>
        <IonContent className="auth-content ion-padding">
          <div className="auth-container">
            <IonSpinner name="crescent" />
            <IonText color="medium">
              <p>Organisatie laden...</p>
            </IonText>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent className="auth-content ion-padding">
        <div className="auth-container">
          <div className="auth-header">
            <AppLogo />
            <IonText color="dark">
              <h1>Kies een organisatie</h1>
              <p>Selecteer de organisatie waarmee je wilt werken</p>
            </IonText>
          </div>

          <IonList className="org-list">
            {organisations.map((org) => (
              <IonItem
                key={org.id}
                className="org-item"
                button
                onClick={() => handleSelectOrganisation(org)}
                disabled={switchMutation.isPending}
              >
                <IonLabel>
                  <h2>{org.name}</h2>
                </IonLabel>
                <IonBadge
                  slot="end"
                  color={getRoleBadgeColor(org.role)}
                  className="org-badge"
                >
                  {getRoleLabel(org.role)}
                </IonBadge>
              </IonItem>
            ))}
          </IonList>

          {switchMutation.error && (
            <IonText color="danger" className="error-text">
              <p style={{ textAlign: "center" }}>
                {getErrorMessage(switchMutation.error)}
              </p>
            </IonText>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Organisations;
